import s from "./LandingSections.module.scss";
import { LandingSinglePageData } from "@/types/strapi/landing";
import { orderLink } from "@/const";
import dynamic from "next/dynamic";
type LandingSectionsProps = {
  sections: LandingSinglePageData["sections"];
};

const SectionWriters = dynamic(() => import("../../shared/sections/SectionWriters"));
const SectionLandingBenefits = dynamic(() => import("../../shared/sections/SectionLandingBenefits"));
const SectionTabList = dynamic(() => import("../../shared/sections/SectionTabList"));
const SectionReviews = dynamic(() => import("../../shared/sections/SectionReviews"));
const SectionPayments = dynamic(() => import("../../shared/sections/SectionPayments"));
const SectionFeatures = dynamic(() => import("../../shared/sections/SectionFeatures"));
const SectionSampleCard = dynamic(() => import("../../shared/sections/SectionSampleCard"));
const SectionLandingHow = dynamic(() => import("../../shared/sections/SectionLandingHow"));
const SectionFaq = dynamic(() => import("../../shared/sections/SectionFaq"));
const SectionOrder = dynamic(() => import("../../shared/sections/SectionOrder"));
const SectionSEO = dynamic(() => import("../../shared/sections/SectionSEO"));

export const LandingSections = ({ sections }: LandingSectionsProps) => {
  if (!sections) return null;
  return (
    <div className={s.wrapper}>
      {sections.map(({ id, __component, ...rest }) => {
        switch (__component) {
          case "landing.section-writers":
            return <SectionWriters key={`${__component}-${id}`} {...rest} />;
          case "landing.section-benefits":
            return <SectionLandingBenefits key={`${__component}-${id}`} {...rest} />;
          case "landing.section-services":
            return (
              <SectionTabList
                key={`${__component}-${id}`}
                heading={rest.heading}
                subheading={rest.subheading}
                tabs={{
                  items: rest.tabs,
                }}
              />
            );
          case "landing.section-reviews":
            return (
              <SectionReviews
                key={`${__component}-${id}`}
                heading={rest.heading}
                subheading={rest.subheading}
                reviews={rest.reviews}
              />
            );
          case "landing.section-payments":
            return (
              <SectionPayments
                key={`${__component}-${id}`}
                type="secondary"
                heading={rest.heading}
                button={{
                  text: rest.buttonText,
                  href: orderLink,
                }}
              />
            );
          case "landing.section-features":
            return (
              <SectionFeatures
                key={`${__component}-${id}`}
                heading={rest.heading}
                subheading={rest.subheading}
                cards={rest.cards}
                featureCard={rest.featureCard}
              />
            );
          case "landing.section-sample":
            return (
              <SectionSampleCard
                key={`${__component}-${id}`}
                heading={rest.heading}
                subheading={rest.subheading}
                sample={rest.sample}
                sampleV2={rest.sampleV2}
              />
            );
          case "landing.section-how":
            return (
              <SectionLandingHow
                key={`${__component}-${id}`}
                heading={rest.heading}
                subheading={rest.subheading}
                items={rest.items}
                button={{
                  href: orderLink,
                  text: rest.buttonText,
                }}
              />
            );
          case "landing.section-faq":
            return (
              <SectionFaq
                key={`${__component}-${id}`}
                heading={rest.heading}
                subheading={rest.subheading}
                faqItems={rest.faq}
                editorContent
                microData
              />
            );
          case "landing.section-order":
            return (
              <SectionOrder
                key={`${__component}-${id}`}
                heading={rest.heading}
                subheading={rest.subheading}
                button={{
                  text: rest.buttonText,
                  href: orderLink,
                }}
              />
            );
          case "landing.section-seo":
            return (
              <SectionSEO
                key={`${__component}-${id}`}
                heading={rest.heading}
                subheading={rest.subheading}
                content={rest.content}
                headingCircles
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
};
